import { Button, Card, FormControl, FormHelperText, FormLabel, TextField } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

import React, { useState } from "react";
import { useTicketContext } from "./TicketContext";

interface RefinedTicketsProps {
  onRefineTicketSubmit: (context: string) => void;
  isLoading: boolean;
}
/**
 * RefinedTickets component renders a form for refining the generated ticket.
 * It prevents the default form submission behavior and calls the onRefineTicketSubmit callback
 * with the current refined context when the form is submitted.
 * The form consists of a text field for the user to enter the refined ticket context and a
 * submit button that sends the request to the server to refine the ticket.
 * why this is in seperate component because to prevent the re-render while typing
 * @returns {React.ReactElement} The rendered component.
 */
const RefinedTickets: React.FC<RefinedTicketsProps> = ({
  onRefineTicketSubmit,
  isLoading
}: RefinedTicketsProps) => {
  const [refinedContext, setRefinedContext] = useState("");

  /**
   * Handles the ticket refinement form submission.
   * Prevents the default form submission behavior and
   * calls the onRefineTicketSubmit callback with the
   * current refined context.
   * @param {React.FormEvent} e - The form event object.
   */

  const handleTicketRefine = (e: React.FormEvent) => {
    e.preventDefault();
    onRefineTicketSubmit(refinedContext);
  };
  const { content } = useTicketContext();

  return (
    <form onSubmit={handleTicketRefine}>
      <FormControl fullWidth>
        <TextField
          disabled={!content.description || !content.title}
          value={refinedContext}
          placeholder="Ask AI to modify the ticket"
          multiline
          minRows={2}
          fullWidth
          sx={{ mb: 1.5 }}
          onChange={(e) => {
            setRefinedContext(e.target.value);
          }}
          inputProps={{
            sx: {
              "&::placeholder": {
                fontSize: "14px",
                opacity: 1
              }
            }
          }}
        />
      </FormControl>

      <Button
        type="submit"
        variant="outlined"
        startIcon={<SendIcon />}
        fullWidth
        disabled={!refinedContext}
        sx={{ mb: 0 }}
      >
        {!isLoading ? "Refine" : "Refining..."}
      </Button>
      <FormHelperText sx={{ color: "text.secondary", fontSize: "1rem" }}>
        Try: "Add more details", "Include acceptance criteria", "Make it high priority", "Focus on
        mobile"
      </FormHelperText>
    </form>
  );
};

export default RefinedTickets;
